import { Autocomplete, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { SupportedTagEntityTypes, Tag } from '../../../store/Tags';
import { INPUT_STYLE, MULTILINE_INPUT_STYLE } from '../../../utils/styleHelpers';
import { TagItem } from './TagItem';
import { ComponentSpinner } from '../Loading/ComponentSpinner';

export interface TagsInputProps {
  entityId: string;
  entityType: SupportedTagEntityTypes;
  availableTags: Tag[];
  selectedTags: Tag[];
  loading: boolean;
  // Narrower than `loading`, which also covers the tag catalog: a chip may only spin for its own write.
  updating?: boolean;
  onTagChange: (tags: (Tag | string)[]) => Promise<void>;
  isGrid?: boolean;
}

export const TagsInput = ({
  availableTags,
  entityId,
  entityType,
  selectedTags,
  loading,
  updating = false,
  onTagChange,
  isGrid = false
}: TagsInputProps) => {
  const [unselectedTags, setUnselectedTags] = useState<Tag[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSelectTag = (e: React.SyntheticEvent<Element, Event>, value: (Tag | string)[] | null) => {
    if (value && Array.isArray(value)) onTagChange(value);
  };

  const getUnselectedTags = () => {
    return availableTags.filter((tag) => !selectedTags.some((selected) => selected.id === tag.id));
  };

  useEffect(() => {
    setUnselectedTags(getUnselectedTags());
  }, [availableTags, selectedTags]);

  // The grid clips at its own scroll container, so a row near its bottom edge comes up — on open only.
  useEffect(() => {
    if (isGrid) formRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [isGrid]);

  return (
    <form
      ref={formRef}
      onSubmit={(e) => e.preventDefault()}
      // Read by App.css: the cell it sits in only stops clipping while this editor is mounted.
      data-tag-editor
      // Chips wrap the input past a grid row's height, so in a grid it floats over the rows below.
      className={isGrid ? 'absolute inset-x-0 top-0' : 'w-full'}>
      <div>
        <Autocomplete
          id={`tags-input-${entityType}-${entityId}`}
          autoHighlight
          fullWidth
          size={isGrid ? 'small' : 'medium'}
          forcePopupIcon={true}
          popupIcon={loading && selectedTags.length === 0 ? <ComponentSpinner /> : undefined}
          options={unselectedTags}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          onChange={handleSelectTag}
          freeSolo
          multiple
          limitTags={isGrid ? 2 : -1}
          getLimitTagsText={(count) => `+${count}`}
          loading={loading}
          loadingText="Updating tags..."
          clearIcon={loading ? <ComponentSpinner /> : undefined}
          groupBy={() => 'Select a tag or create one'}
          value={selectedTags}
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          renderInput={(params) => (
            <TextField
              {...params}
              data-cy="tags-list"
              aria-label="tags list"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={true}
              InputProps={{ ...params.InputProps, style: { fontSize: '.875rem', outline: '#3B6FE8' }, name: 'Tags' }}
              // A pinned height would leave the part of the floating box below the first chip row transparent.
              className={isGrid ? MULTILINE_INPUT_STYLE : INPUT_STYLE}
              onKeyDown={(e) => {
                if (e.code === 'Space') {
                  e.stopPropagation();
                }
              }}
            />
          )}
          renderTags={(
            value: readonly Tag[],
            getTagProps: (params: { index: number }) => {
              key: number;
              onDelete: (tag: Tag) => void;
              [key: number]: unknown;
            }
          ) => {
            return value.map((option: Tag, index: number) => {
              const { key, onDelete } = getTagProps({ index });
              return (
                <div
                  key={key}
                  className="px-1">
                  <TagItem
                    tag={option}
                    interactive
                    updating={updating}
                    onClick={(tag) => onDelete?.(tag)}
                  />
                </div>
              );
            });
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.id}
              data-testid={option.id}
              className="py-1 pl-4 cursor-pointer hover:bg-grey-1">
              <div className="flex items-start">
                <TagItem tag={option} />
              </div>
            </li>
          )}
        />
      </div>
    </form>
  );
};
